import React, { useState } from "react";
import styles from "../styles/GameHashDisplay.module.css";

interface GameHashDisplayProps {
  gameSeedHash: string;
  gameSeed?: string;
  isGameCompleted?: boolean;
}

const GameHashDisplay: React.FC<GameHashDisplayProps> = ({
  gameSeedHash,
  gameSeed,
  isGameCompleted = false,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [copiedItem, setCopiedItem] = useState<"hash" | "seed" | null>(null);

  const handleCopyHash = () => {
    navigator.clipboard.writeText(gameSeedHash);
    setCopiedItem("hash");
    setTimeout(() => setCopiedItem(null), 1000);
  };

  const handleCopySeed = () => {
    if (gameSeed) {
      navigator.clipboard.writeText(gameSeed);
      setCopiedItem("seed");
      setTimeout(() => setCopiedItem(null), 1000);
    }
  };

  const tooltipContent =
    isGameCompleted && gameSeed ? (
      <>
        <h4>PROVABLY FAIR VERIFICATION</h4>
        <p>GAME DATA REVEALED:</p>
        <ul>
          <li>
            <strong>GAME SEED:</strong> SECRET THAT DETERMINED DEATH TILES
          </li>
          <li>
            <strong>GAME HASH:</strong> HASH OF GAME DATA, REVEALED ON START
          </li>
          <li>VERIFY THE GAME WAS COMPLETELY FAIR</li>
          <li>COPY BOTH VALUES TO VERIFY INDEPENDENTLY</li>
        </ul>
        <p>CLICK "VERIFY GAME" TO CHECK FAIRNESS</p>
      </>
    ) : isGameCompleted ? (
      <>
        <h4>GAME HASH</h4>
        <p>GENERATED BEFORE GAME START:</p>
        <ul>
          <li>DEATH TILE POSITIONS PRE-DETERMINED</li>
          <li>COULD NOT BE CHANGED DURING GAMEPLAY</li>
          <li>VERIFY THIS GAME WAS FAIR</li>
          <li>COPY HASH TO VERIFY ANYTIME</li>
        </ul>
        <p>GAME SEED REVEALED WHEN AVAILABLE</p>
      </>
    ) : (
      <>
        <h4>GAME HASH</h4>
        <p>GENERATED BEFORE GAME START:</p>
        <ul>
          <li>DEATH TILE POSITIONS PRE-DETERMINED</li>
          <li>CANNOT BE CHANGED DURING GAMEPLAY</li>
          <li>VERIFY AFTER GAME ENDS</li>
          <li>COPY HASH TO VERIFY LATER</li>
        </ul>
        <p>ENSURES COMPLETE FAIRNESS</p>
      </>
    );

  return (
    <div
      className={`${styles.gameHashContainer} ${!(isGameCompleted && gameSeed) ? styles.singleItem : ""}`}
      onClick={() => setIsTooltipVisible(!isTooltipVisible)}
      onMouseEnter={() => setIsTooltipVisible(true)}
      onMouseLeave={() => setIsTooltipVisible(false)}
    >
      {/* Game Hash Display */}
      <div className={styles.hashRow}>
        <span className={styles.hashLabel}>
          <span className={styles.hashLabelDesktop}>HASH:</span>
          <span className={styles.hashLabelMobile}>HASH:</span>
        </span>
        <span className={styles.hashValue}>
          <span className={styles.hashValueDesktop}>
            {gameSeedHash.slice(0, 12)}...{gameSeedHash.slice(-8)}
          </span>
          <span className={styles.hashValueMobile}>
            {gameSeedHash.slice(0, 8)}...{gameSeedHash.slice(-6)}
          </span>
        </span>
        <button
          className={styles.hashCopyBtn}
          onClick={(e) => {
            e.stopPropagation();
            handleCopyHash();
          }}
          title="Copy full hash"
        >
          {copiedItem === "hash" ? "DONE" : "COPY"}
        </button>
      </div>

      {/* Game Seed Display (only when revealed) */}
      {isGameCompleted && gameSeed && (
        <div className={styles.seedRow}>
          <span className={styles.hashLabel}>
            <span className={styles.hashLabelDesktop}>SEED:</span>
            <span className={styles.hashLabelMobile}>SEED:</span>
          </span>
          <span className={styles.hashValue}>
            <span className={styles.hashValueDesktop}>
              {gameSeed.slice(0, 12)}...{gameSeed.slice(-8)}
            </span>
            <span className={styles.hashValueMobile}>
              {gameSeed.slice(0, 8)}...{gameSeed.slice(-6)}
            </span>
          </span>
          <button
            className={styles.hashCopyBtn}
            onClick={(e) => {
              e.stopPropagation();
              handleCopySeed();
            }}
            title="Copy full seed"
          >
            {copiedItem === "seed" ? "DONE" : "COPY"}
          </button>
        </div>
      )}

      <div className={`${styles.tooltipContent} ${isTooltipVisible ? styles.visible : ""}`}>
        {tooltipContent}
      </div>
    </div>
  );
};

export default GameHashDisplay;
